// API service for fetching data
import authService from "../services/authService";
export const API_ROOT = process.env.REACT_APP_API_BASE_URL || 'https://uni-func-pd-thrive360.azurewebsites.net/api';
const API_BASE_URL = `${API_ROOT}/stocks`;
/** Base URL for OpenAI-compatible proxy routes (LangChain ChatOpenAI `configuration.baseURL`). */
export const OPENAI_PROXY_BASE_URL = `${API_ROOT}/openai/v1`;

const authHeaders = async (extra = {}) => {
  const idToken = await authService.getIdToken();
  return { ...extra, Authorization: `Bearer ${idToken}` };
};

/**
 * Fetch country data from the API
 */
export const fetchCountryData = async (iso3 = 'NGA', antigen = null) => {
  try {
    let url = `${API_BASE_URL}?iso3=${iso3}`;
    if (antigen && antigen !== 'all') {
      url += `&antigen=${encodeURIComponent(antigen)}`;
    }
    
    console.log(`API call URL: ${url}`);
    const response = await fetch(url, { 
      mode: 'cors',
      headers: await authHeaders()
    });
    console.log('Response OK');
    
    if (!response.ok) {
      if (response.status === 404) {
        console.warn('No country data found');
        return [];
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching country data:', error);
    throw error;
  }
};

/**
 * Fetch store data from the API
 */
export const fetchStoreData = async (storeId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/storeid/${storeId}`,
      { mode: 'cors', headers: await authHeaders() }
    );

    // Check if response is not OK
    if (!response.ok) {
      // If it's a 404 or similar, treat it as a "no data" case.
      if (response.status === 404) {
        console.warn(`No data found for store ${storeId}`);
        return null;
      }
      console.warn(`Store ${storeId} returned status: ${response.status}`);
      return null; // Return null instead of throwing error
    }

    const json = await response.json();
    if (json.error) {
      console.warn(`Store ${storeId} returned error: ${json.error}`);
      return null;
    }
    return json;
  } catch (error) {
    // For network errors or other exceptions, log but don't throw
    console.warn(`Skipping store ${storeId} due to error: ${error.message}`);
    return null; // Return null instead of re-throwing
  }
};

export const fetchScipQuery = async (query, params = {}) => {
  const baseUrl = API_ROOT;
  const idToken = await authService.getIdToken();
  const response = await fetch(`${baseUrl}/backend_scip-query`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    },
    body: JSON.stringify({ query, params }),
    mode: 'cors'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error || errorData?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  if (result?.success === false) {
    throw new Error(result?.error || 'SCIP query failed');
  }
  return result?.data?.rows || result?.rows || [];
};

export const deleteScipCip = async (cipId) => {
  const baseUrl = API_ROOT;
  const idToken = await authService.getIdToken();
  const response = await fetch(`${baseUrl}/backend_scip-delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    },
    body: JSON.stringify({ cipId }),
    mode: 'cors'
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.error || errorData?.message || `HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  if (result?.success === false) {
    throw new Error(result?.error || 'Failed to delete CIP');
  }
  return result?.data || result;
};

// Ticket API functions
export const fetchTickets = async (userRole, countryCode = null, filters = {}) => {
  try {
    let url = `${API_ROOT}/getTickets?`;
    const params = new URLSearchParams();
    
    if (countryCode) {
      params.append('country_code', countryCode);
    }
    
    // Add other filters
    if (filters.status) params.append('status', filters.status);
    if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
    if (filters.creator) params.append('creator', filters.creator);
    if (filters.priority) params.append('priority', filters.priority);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    
    const response = await fetch(`${url}${params.toString()}`, {
      mode: 'cors',
      headers: await authHeaders()
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // Support both { tickets } and { data: { tickets } } shapes
    const tickets = result?.data?.tickets ?? result?.tickets ?? [];
    return tickets;
  } catch (error) {
    console.error('Error fetching tickets:', error);
    throw error;
  }
};

export const createTicket = async (ticketData) => {
  try {
    const response = await fetch(`${API_ROOT}/createTicket`, {
      method: 'POST',
      headers: {
        ...(await authHeaders({ 'Content-Type': 'application/json' }))
      },
      body: JSON.stringify(ticketData),
      mode: 'cors'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // Some endpoints return data directly, others wrap it in { data: ... }
    return result?.data?.ticket ?? result?.ticket ?? result;
  } catch (error) {
    console.error('Error creating ticket:', error);
    throw error;
  }
};

export const runMonthlyFlagsTickets = async () => {
  try {
    const response = await fetch(`${API_ROOT}/backend_monthlyFlagsTickets`, {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      mode: 'cors'
    });

    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result?.error || result?.message || `HTTP error! status: ${response.status}`);
    }

    return result?.data || result;
  } catch (error) {
    console.error('Error running monthly flags tickets:', error);
    throw error;
  }
};

export const updateTicket = async (ticketId, updates) => {
  try {
    const response = await fetch(`${API_ROOT}/updateTicket/${ticketId}`, {
      method: 'PUT',
      headers: {
        ...(await authHeaders({ 'Content-Type': 'application/json' }))
      },
      body: JSON.stringify(updates),
      mode: 'cors'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result?.data?.ticket ?? result?.ticket ?? result;
  } catch (error) {
    console.error('Error updating ticket:', error);
    throw error;
  }
};

export const addComment = async (ticketId, comment) => {
  try {
    const response = await fetch(`${API_ROOT}/addComment`, {
      method: 'POST',
      headers: {
        ...(await authHeaders({ 'Content-Type': 'application/json' }))
      },
      body: JSON.stringify({
        ticketId,
        ...comment
      }),
      mode: 'cors'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // Support both { comment } and { data: { comment } } response shapes
    return result?.data?.comment ?? result?.comment ?? result;
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

// RACI Matrix API functions
export const fetchRaciMatrix = async (countryCode) => {
  try {
    const response = await fetch(`${API_ROOT}/raciMatrix/${countryCode}`, { 
      mode: 'cors',
      headers: await authHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching RACI Matrix:', error);
    throw error;
  }
};

export const updateRaciMatrix = async (countryCode, raciMatrix) => {
  try {
    const response = await fetch(`${API_ROOT}/raciMatrix/${countryCode}`, {
      method: 'PUT',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(raciMatrix),
      mode: 'cors'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating RACI Matrix:', error);
    throw error;
  }
};

// SLA Configuration API functions
export const fetchSlaConfig = async (countryCode) => {
  try {
    const response = await fetch(`${API_ROOT}/slaConfig/${countryCode}`, { 
      mode: 'cors',
      headers: await authHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching SLA configuration:', error);
    throw error;
  }
};

export const updateSlaConfig = async (countryCode, slaConfig) => {
  try {
    const response = await fetch(`${API_ROOT}/slaConfig/${countryCode}`, {
      method: 'PUT',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(slaConfig),
      mode: 'cors'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating SLA configuration:', error);
    throw error;
  }
};

// ===== Email Templates API =====
export const fetchEmailTemplates = async (container = 'email-templates', blob = 'email-templates.json') => {
  try {
    const response = await fetch(`${API_ROOT}/blob/${encodeURIComponent(container)}/${encodeURIComponent(blob)}`, {
      mode: 'cors',
      headers: await authHeaders()
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching email templates:', error);
    throw error;
  }
};

export const updateEmailTemplate = async (templateName, content, container = 'email-templates', blob = 'email-templates.json') => {
  try {
    const response = await fetch(`${API_ROOT}/blob/${encodeURIComponent(container)}/${encodeURIComponent(blob)}`, {
      method: 'PUT',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ templateName, content }),
      mode: 'cors'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error updating email template:', error);
    throw error;
  }
};

// ===== Email Sending API =====
export const sendEmail = async (emailData) => {
  try {
    const response = await fetch(`${API_ROOT}/sendEmail`, {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(emailData),
      mode: 'cors'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

// ===== Guest Ticket (public) API =====
export const fetchTicketByToken = async (token) => {
  try {
    const response = await fetch(`${API_ROOT}/public/getTicket/${token}`, {
      mode: 'cors',
      headers: await authHeaders()
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // Allow { ticket } or direct ticket
    return result?.ticket ?? result;
  } catch (error) {
    console.error('Error fetching ticket by token:', error);
    throw error;
  }
};

export const addCommentByToken = async (token, commentData) => {
  try {
    const response = await fetch(`${API_ROOT}/addComment/${token}`, {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(commentData),
      mode: 'cors'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // Support { comment } or { data: { comment } }
    return result?.data?.comment ?? result?.comment ?? result;
  } catch (error) {
    console.error('Error adding comment by token:', error);
    throw error;
  }
};

export const uploadAttachmentToBlob = async (file, container = 'data-dumps', blobName) => {
  blobName = blobName || `${Date.now()}-${file.name}`;
  const endpoint = `${API_ROOT}/blob/${encodeURIComponent(container)}/${encodeURIComponent(blobName)}`;

  // Server-managed upload: send raw bytes; function writes with correct content-type
  const bytes = new Uint8Array(await file.arrayBuffer());
  const res = await fetch(endpoint, {
    method: 'PUT',
    headers: await authHeaders({ 'Content-Type': 'application/octet-stream' }),
    body: bytes,
    mode: 'cors'
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || `Upload failed: ${res.status}`);
  }
  const result = await res.json().catch(() => ({}));
  const url = result.url || result.path || `https://rgpdthrive3609cee.blob.core.windows.net/${encodeURIComponent(container)}/${encodeURIComponent(blobName)}`;
  return { container, blobName, url, path: url };
};

// ===== Inventories Upload + Mapping APIs =====
export const uploadInventoryFile = async (file) => {
  const blobName = `uploads/${Date.now()}-${file.name}`;
  return uploadAttachmentToBlob(file, 'inventories', blobName);
};

export const saveInventoryMappingConfig = async (config, mappingId) => {
  const id = mappingId || Date.now().toString();
  const container = 'inventories';
  const blob = `mappings/${id}.json`;
  const endpoint = `${API_ROOT}/blob/${encodeURIComponent(container)}/${encodeURIComponent(blob)}`;
  const response = await fetch(endpoint, {
    method: 'PUT',
    headers: await authHeaders({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(config),
    mode: 'cors'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to save mapping config: ${response.status}`);
  }
  const result = await response.json().catch(() => ({}));
  return { id, container, blob, ...result };
};

export const ingestInventoryRecords = async (payload) => {
  const endpoint = `${API_ROOT}/ingestInventory`;
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: await authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
    mode: 'cors'
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to ingest inventory: ${response.status}`);
  }
  return response.json();
};

// ===== Inventories Summary API =====
export const fetchInventoryCountrySummary = async () => {
  try {
    const endpoint = `${API_ROOT}/inventorySummary`;
    const res = await fetch(endpoint, {
      mode: 'cors',
      headers: await authHeaders()
    });
    if (!res.ok) {
      if (res.status === 404) return [];
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `Failed to fetch inventory summary: ${res.status}`);
    }
    const data = await res.json();
    return Array.isArray(data) ? data : (data?.items || []);
  } catch (e) {
    console.error('Error fetching inventory summary:', e);
    return [];
  }
};

export const fetchTicketById = async (ticketId) => {
  try {
    const response = await fetch(`${API_ROOT}/getTicket/${ticketId}`, {
      mode: 'cors',
      headers: await authHeaders()
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    // Allow { ticket } or direct ticket object
    return result?.ticket ?? result;
  } catch (error) {
    console.error('Error fetching ticket by id:', error);
    throw error;
  }
};

// ===== Projects & Tasks API =====

export const fetchProjects = async (filters = {}) => {
  try {
    const base = `${API_ROOT}/getProjects?`;
    const params = new URLSearchParams();
    if (filters.country_code) params.append('country_code', filters.country_code);
    if (filters.status) params.append('status', filters.status);
    if (filters.owner_id) params.append('owner_id', filters.owner_id);
    if (filters.limit) params.append('limit', String(filters.limit));
    if (filters.offset) params.append('offset', String(filters.offset));
    const res = await fetch(`${base}${params.toString()}`, {
      mode: 'cors',
      headers: await authHeaders()
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const json = await res.json();
    return json?.projects ?? json ?? [];
  } catch (e) {
    console.error('Error fetching projects:', e);
    throw e;
  }
};

export const createProjectApi = async (payload) => {
  try {
    const res = await fetch(`${API_ROOT}/createProject`, {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
      mode: 'cors'
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const json = await res.json();
    return json?.project ?? json;
  } catch (e) {
    console.error('Error creating project:', e);
    throw e;
  }
};

export const fetchProjectTasks = async (filters = {}) => {
  try {
    const base = `${API_ROOT}/getProjectTasks?`;
    const params = new URLSearchParams();
    if (filters.project_id) params.append('project_id', filters.project_id);
    if (filters.status) params.append('status', filters.status);
    if (filters.assignee_id) params.append('assignee_id', filters.assignee_id);
    if (filters.country_code) params.append('country_code', filters.country_code);
    if (filters.limit) params.append('limit', String(filters.limit));
    if (filters.offset) params.append('offset', String(filters.offset));
    const res = await fetch(`${base}${params.toString()}`, {
      mode: 'cors',
      headers: await authHeaders()
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const json = await res.json();
    return json?.tasks ?? json ?? [];
  } catch (e) {
    console.error('Error fetching project tasks:', e);
    throw e;
  }
};

export const createProjectTaskApi = async (payload) => {
  try {
    const res = await fetch(`${API_ROOT}/createProjectTask`, {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
      mode: 'cors'
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const json = await res.json();
    return json?.task ?? json;
  } catch (e) {
    console.error('Error creating project task:', e);
    throw e;
  }
};

export const updateProjectTaskApi = async (taskId, updates) => {
  try {
    const res = await fetch(`${API_ROOT}/updateProjectTask/${taskId}`, {
      method: 'PUT',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(updates),
      mode: 'cors'
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const json = await res.json();
    return json?.task ?? json;
  } catch (e) {
    console.error('Error updating project task:', e);
    throw e;
  }
};

export const fetchTags = async () => {
  try {
    const res = await fetch(`${API_ROOT}/getTags`, {
      mode: 'cors',
      headers: await authHeaders()
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const json = await res.json();
    return json?.tags ?? json ?? [];
  } catch (e) {
    console.error('Error fetching tags:', e);
    throw e;
  }
};

export const createTagApi = async (payload) => {
  try {
    const res = await fetch(`${API_ROOT}/createTag`, {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
      mode: 'cors'
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const json = await res.json();
    return json?.tag ?? json;
  } catch (e) {
    console.error('Error creating tag:', e);
    throw e;
  }
};

export const attachTagApi = async ({ target_type, target_id, tag_id }) => {
  try {
    const res = await fetch(`${API_ROOT}/attachTag`, {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ target_type, target_id, tag_id }),
      mode: 'cors'
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    return true;
  } catch (e) {
    console.error('Error attaching tag:', e);
    throw e;
  }
};

export const detachTagApi = async ({ target_type, target_id, tag_id }) => {
  try {
    const res = await fetch(`${API_ROOT}/detachTag`, {
      method: 'POST',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ target_type, target_id, tag_id }),
      mode: 'cors'
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    return true;
  } catch (e) {
    console.error('Error detaching tag:', e);
    throw e;
  }
};

// ===== Users API (for assigning owners/assignees) =====
export const fetchUsers = async () => {
  try {
    const idToken = await authService.getIdToken();
    const res = await fetch(`${API_ROOT}/getUsers`, {
      mode: 'cors',
      headers: { Authorization: `Bearer ${idToken}` }
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const json = await res.json();
    // Support { users } or raw array
    return json?.users ?? json ?? [];
  } catch (e) {
    console.error('Error fetching users:', e);
    return [];
  }
};

export const updateProjectApi = async (projectId, updates) => {
  try {
    const res = await fetch(`${API_ROOT}/updateProject/${projectId}`, {
      method: 'PUT',
      headers: await authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(updates),
      mode: 'cors'
    });
    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
    const json = await res.json();
    return json?.project ?? json;
  } catch (e) {
    console.error('Error updating project:', e);
    throw e;
  }
};